/* =============================================================================
 * M.M.LaFleur Gifting page (v4.7.9, Oct 22 2026). Behaviour for the mm_gift_*
 * sections in templates/page.gifting.json:
 *
 *   [data-gf-finder]  "Who are you shopping for?" tabs (ARIA tabs, arrow keys).
 *   [data-gf-box]     Build your own gift box: slots for a hero piece and a
 *                     finishing touch; a slot opens a pop-up (<dialog>) of
 *                     product cards with an optional full-catalog search
 *                     (predictive search); a gift note; both pieces go to
 *                     the bag in one request.
 *   [data-gf-set]     Curated sets: choose sizes where needed, add every piece
 *                     to the bag in one request.
 *   [data-gf-egift]   E-gift card amount chips: points the button at the
 *                     matching gift card variant (the card itself is bought on
 *                     its product page, which carries the recipient form).
 *
 * Bag: POST /cart/add.js with every line at once, then the side cart is
 * refreshed and opened through mm-cart.js (window.MMCart), falling back to the
 * mm:cart:refresh event. Box lines share a hidden _gift_box id so the pair can
 * be found in an order; the note travels as a visible "Gift note" property on
 * the hero piece.
 *
 * Safe to load more than once (Barba soft navigation re-runs scripts): every
 * root is marked when bound.
 * ========================================================================== */
(function () {
  "use strict";

  function money(cents) {
    var v = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v);
    } catch (e) { return "$" + v.toFixed(v % 1 ? 2 : 0); }
  }

  function parse(json, fallback) {
    try { return JSON.parse(json); } catch (e) { return fallback; }
  }

  function refreshAndOpenCart() {
    var cart = window.MMCart;
    var done = function () {
      if (cart && typeof cart.open === "function") cart.open();
      else { try { document.dispatchEvent(new CustomEvent("mm:cart:open")); } catch (e) {} }
    };
    try {
      if (cart && typeof cart.refresh === "function") {
        var p = cart.refresh();
        if (p && typeof p.then === "function") { p.then(done, done); return; }
      } else {
        document.dispatchEvent(new CustomEvent("mm:cart:refresh"));
      }
    } catch (e) {}
    setTimeout(done, 350);
  }

  function addToCart(items) {
    return fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error((data && (data.description || data.message)) || "Could not add to bag.");
        return data;
      });
    });
  }

  function track(name, data) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data || {})); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {}); } catch (e) {}
  }

  /* A size <select> for a product with more than one variant. Returns null for
     one-variant products (the variant is used as is). */
  function variantSelect(variants, optionName, label, idBase) {
    var avail = variants.filter(function (v) { return v.available; });
    if (variants.length <= 1) return null;
    var wrap = document.createElement("div");
    var id = idBase + "-" + Math.random().toString(36).slice(2, 7);
    var lab = document.createElement("label");
    lab.className = "gf-field-label";
    lab.setAttribute("for", id);
    lab.textContent = label || (optionName ? optionName : "Size");
    var sel = document.createElement("select");
    sel.className = "gf-select";
    sel.id = id;
    sel.setAttribute("data-gf-variant-select", "");
    var ph = document.createElement("option");
    ph.value = "";
    ph.textContent = "Choose " + (optionName ? optionName.toLowerCase() : "size");
    sel.appendChild(ph);
    variants.forEach(function (v) {
      var o = document.createElement("option");
      o.value = v.id;
      o.textContent = v.title + (v.available ? "" : " (sold out)");
      o.disabled = !v.available;
      sel.appendChild(o);
    });
    if (avail.length === 1) sel.value = String(avail[0].id);
    wrap.appendChild(lab);
    wrap.appendChild(sel);
    return wrap;
  }

  /* ----------------------------------------------------------- FINDER TABS */
  function bindFinder(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute("aria-controls"));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
      track("gifting_recipient_tab", { tab: tab.textContent.trim() });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener("click", function () { select(t, false); });
      t.addEventListener("keydown", function (e) {
        var k = e.key, n = null;
        if (k === "ArrowRight") n = tabs[(i + 1) % tabs.length];
        else if (k === "ArrowLeft") n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (k === "Home") n = tabs[0];
        else if (k === "End") n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });
  }

  /* ------------------------------------------------------------- GIFT BOX */
  /* Slots (what's in the box) + a tabbed picker (what you can choose) + an
     optional catalog search. A pick is always a checked radio in that step's
     panel, so everything else reads from one place. */
  function esc(s) {
    return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function pickCard(name, data) {
    var label = document.createElement("label");
    label.className = "gf-pick";
    label.innerHTML =
      '<input type="radio" name="' + esc(name) + '" value="' + esc(data.id) + '"' +
      ' data-title="' + esc(data.title) + '" data-colour="' + esc(data.colour) + '"' +
      ' data-price="' + esc(data.price) + '" data-img="' + esc(data.img) + '"' +
      ' data-option="' + esc(data.option) + '" data-variants="' + esc(JSON.stringify(data.variants)) + '">' +
      '<span class="gf-pick-img">' + (data.img ? '<img src="' + esc(data.img) + '" alt="" loading="lazy">' : "") +
      '<span class="gf-pick-check" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7"/></svg></span></span>' +
      '<span class="gf-pick-name">' + esc(data.title) + "</span>" +
      '<span class="gf-pick-meta">' + (data.colour ? esc(data.colour) + " &middot; " : "") + esc(money(data.price)) + "</span>";
    return label;
  }

  function splitTitle(t) {
    var parts = String(t || "").split(" :: ");
    /* Drop quoted nicknames ('Giselle "Something's Gotta Give" Cable Sweater'
       reads as "Giselle Cable Sweater") so names fit a card. */
    var name = parts[0].split(" - ")[0].replace(/\s*"[^"]*"\s*/g, " ").replace(/\s+/g, " ").trim();
    return { name: name, colour: parts.length > 1 ? parts[parts.length - 1].trim() : "" };
  }

  function bindBox(root) {
    var boxId = root.getAttribute("data-box-id");
    var panels = Array.prototype.slice.call(root.querySelectorAll("[data-gf-panel]"));
    var slots = Array.prototype.slice.call(root.querySelectorAll("[data-gf-slot]"));
    var bar = root.querySelector("[data-gf-progress]");
    var label = root.querySelector("[data-gf-progress-label]");
    var totalEl = root.querySelector("[data-gf-total]");
    var summaryEl = root.querySelector("[data-gf-summary]");
    var btn = root.querySelector("[data-gf-add]");
    var status = root.querySelector("[data-gf-status]");
    var note = root.querySelector("[data-gf-note]");
    var count = root.querySelector("[data-gf-note-count]");
    var modal = root.querySelector("[data-gf-modal]");
    var modalK = root.querySelector("[data-gf-modal-k]");
    var modalTitle = root.querySelector("[data-gf-modal-title]");
    var modalLabel = root.querySelector("[data-gf-modal-label]");
    var modalBody = root.querySelector("[data-gf-modal-body]");
    var search = root.querySelector("[data-gf-search]");
    var results = root.querySelector("[data-gf-results]");
    var searchStatus = root.querySelector("[data-gf-search-status]");
    var labels = [root.getAttribute("data-label-0"), root.getAttribute("data-label-1"), root.getAttribute("data-label-2")];
    var current = 1;

    function panel(n) { return root.querySelector('[data-gf-panel="' + n + '"]'); }
    function slot(n) { return root.querySelector('[data-gf-slot="' + n + '"]'); }

    function chosen(n) {
      var p = panel(n);
      var input = p && p.querySelector('input[type="radio"]:checked');
      if (!input) return null;
      var vars = parse(input.getAttribute("data-variants"), []);
      var sel = slot(n) && slot(n).querySelector("[data-gf-variant-select]");
      var id = null;
      if (vars.length === 1) id = vars[0].available ? vars[0].id : null;
      else if (sel && sel.value) id = Number(sel.value);
      var price = Number(input.getAttribute("data-price")) || 0;
      if (id) vars.forEach(function (v) { if (v.id === id) price = v.price; });
      return { input: input, id: id, price: price, title: input.getAttribute("data-title") };
    }

    /* Show step n's picks in the pop-up (hidden while search results show). */
    function showStep(n) {
      current = n;
      var searching = results && !results.hidden;
      panels.forEach(function (p) { p.hidden = searching || Number(p.getAttribute("data-gf-panel")) !== n; });
      if (modalLabel) modalLabel.hidden = searching;
      var p = panel(n);
      if (p && modalK) modalK.textContent = p.getAttribute("data-k") || "";
      if (p && modalTitle) modalTitle.textContent = p.getAttribute("data-title") || "";
    }

    function isOpen() { return modal && (modal.open || modal.hasAttribute("open")); }
    function openStep(n) {
      if (!modal) return;
      clearSearch();
      showStep(n);
      if (modalBody) modalBody.scrollTop = 0;
      if (!isOpen()) {
        if (typeof modal.showModal === "function") modal.showModal(); else modal.setAttribute("open", "");
      }
      document.documentElement.classList.add("gf-modal-lock");
      track("gifting_box_open", { step: n });
    }
    function closeModal() {
      if (!isOpen()) return;
      if (typeof modal.close === "function") modal.close(); else { modal.removeAttribute("open"); onClosed(); }
    }
    function onClosed() {
      document.documentElement.classList.remove("gf-modal-lock");
      clearSearch();
    }

    function update() {
      var picked = 0, ready = 0, total = 0, names = [], next = null;
      slots.forEach(function (s) {
        var n = Number(s.getAttribute("data-gf-slot"));
        var c = chosen(n);
        s.setAttribute("data-done", c && c.id ? "true" : "false");
        if (c && c.id) s.removeAttribute("data-error");
        if (c) { picked++; total += c.price; names.push(c.title); }
        else if (next === null) next = n;
        if (c && c.id) ready++;
      });
      /* Outline the next empty slot so the shopper knows where to go. */
      slots.forEach(function (s) { s.setAttribute("data-active", Number(s.getAttribute("data-gf-slot")) === next ? "true" : "false"); });
      var stage = ready === slots.length ? 2 : (picked ? 1 : 0);
      if (label) label.innerHTML = picked === slots.length && ready < picked
        ? "<strong>Choose a size</strong> to finish your gift set"
        : (labels[stage] || "");
      if (bar) bar.style.width = (picked / slots.length * 100) + "%";
      if (summaryEl) summaryEl.textContent = names.length ? names.join(" + ") + " ·" : "";
      if (totalEl) totalEl.textContent = picked ? money(total) : "";
      /* The button goes dark once both pieces are picked; a missing size is
         caught on click and pointed out, rather than leaving it greyed out. */
      if (btn) btn.disabled = picked < slots.length;
      if (ready === slots.length && status && status.getAttribute("data-tone") === "error") { status.textContent = ""; status.removeAttribute("data-tone"); }
    }

    function fillSlot(n, input) {
      var s = slot(n);
      if (!s) return;
      var media = s.querySelector("[data-gf-slot-media]");
      var name = s.querySelector("[data-gf-slot-name]");
      var meta = s.querySelector("[data-gf-slot-meta]");
      var change = s.querySelector("[data-gf-slot-change]");
      var size = s.querySelector("[data-gf-variant-slot]");
      s.setAttribute("data-filled", "true");
      if (media) {
        var img = input.getAttribute("data-img");
        media.innerHTML = img ? '<img src="' + esc(img) + '" alt="">' : "";
      }
      if (name) name.textContent = input.getAttribute("data-title") || "";
      if (meta) {
        var colour = input.getAttribute("data-colour");
        meta.textContent = (colour ? colour + " · " : "") + money(input.getAttribute("data-price"));
      }
      if (change) change.textContent = "Change";
      if (size) {
        size.innerHTML = "";
        var sel = variantSelect(parse(input.getAttribute("data-variants"), []), input.getAttribute("data-option"), null, "gf-box");
        if (sel) size.appendChild(sel);
      }
    }

    panels.forEach(function (p) {
      p.addEventListener("change", function (e) {
        var input = e.target.closest('input[type="radio"]');
        if (!input) return;
        var n = Number(p.getAttribute("data-gf-panel"));
        fillSlot(n, input);
        update();
        /* Let the check mark register, then close and put focus on the size
           menu if this piece needs one. */
        setTimeout(function () {
          closeModal();
          var sel = slot(n) && slot(n).querySelector("[data-gf-variant-select]");
          if (sel && !sel.value) { try { sel.focus({ preventScroll: true }); } catch (err) { sel.focus(); } }
        }, 260);
      });
    });

    slots.forEach(function (s) {
      s.addEventListener("change", update);
      var go = s.querySelector("[data-gf-goto]");
      if (go) go.addEventListener("click", function () { openStep(Number(go.getAttribute("data-gf-goto"))); });
    });

    if (modal) {
      var x = modal.querySelector("[data-gf-modal-close]");
      if (x) x.addEventListener("click", closeModal);
      modal.addEventListener("close", onClosed);
      /* A click on the backdrop lands on the dialog itself. */
      modal.addEventListener("click", function (e) { if (e.target === modal) closeModal(); });
      modal.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && search && search.value && document.activeElement === search) { e.preventDefault(); clearSearch(); }
      });
    }

    /* Catalog search: Shopify predictive search, in-stock products only. A
       result is turned into a card in the open step's panel and picked. */
    var timer = null, seq = 0;
    function clearSearch() {
      seq++;
      if (results) { results.hidden = true; results.innerHTML = ""; }
      if (searchStatus) { searchStatus.hidden = true; searchStatus.textContent = ""; }
      if (search && search.value) search.value = "";
      showStep(current);
    }
    function runSearch(q) {
      var my = ++seq;
      if (searchStatus) { searchStatus.hidden = false; searchStatus.textContent = "Searching…"; }
      if (modalLabel) modalLabel.hidden = true;
      var url = (window.Shopify && Shopify.routes && Shopify.routes.root || "/") +
        "search/suggest.json?q=" + encodeURIComponent(q) +
        "&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide";
      fetch(url, { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }).then(function (data) {
        if (my !== seq) return;
        var list = (((data || {}).resources || {}).results || {}).products || [];
        list = list.filter(function (p) { return p.available && String(p.type || "").toLowerCase() !== "gift card" && !/try-on/i.test(p.title); });
        results.innerHTML = "";
        list.forEach(function (p) {
          var t = splitTitle(p.title);
          var b = document.createElement("button");
          b.type = "button";
          b.className = "gf-pick gf-pick--result";
          b.setAttribute("data-handle", p.handle);
          b.innerHTML = '<span class="gf-pick-img">' + (p.image ? '<img src="' + esc(p.image) + (p.image.indexOf("?") > -1 ? "&" : "?") + 'width=400" alt="" loading="lazy">' : "") + "</span>" +
            '<span class="gf-pick-name">' + esc(t.name) + "</span>" +
            '<span class="gf-pick-meta">' + (t.colour ? esc(t.colour) + " &middot; " : "") + esc(money(Math.round(parseFloat(p.price) * 100))) + "</span>";
          results.appendChild(b);
        });
        results.hidden = false;
        panels.forEach(function (pn) { pn.hidden = true; });
        if (modalBody) modalBody.scrollTop = 0;
        if (searchStatus) {
          searchStatus.hidden = false;
          searchStatus.textContent = list.length ? list.length + " result" + (list.length === 1 ? "" : "s") + " for “" + q + "”" : "No in-stock pieces match “" + q + "”.";
        }
      }).catch(function () {
        if (searchStatus) { searchStatus.hidden = false; searchStatus.textContent = "Search isn't available right now."; }
      });
    }
    if (search) {
      search.addEventListener("input", function () {
        clearTimeout(timer);
        var q = search.value.trim();
        if (q.length < 2) { seq++; results.hidden = true; results.innerHTML = ""; if (searchStatus) searchStatus.hidden = true; showStep(current); return; }
        timer = setTimeout(function () { runSearch(q); }, 250);
      });
    }
    if (results) results.addEventListener("click", function (e) {
      var b = e.target.closest("[data-handle]");
      if (!b) return;
      var handle = b.getAttribute("data-handle");
      b.disabled = true;
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "products/" + encodeURIComponent(handle) + ".js")
        .then(function (r) { return r.json(); })
        .then(function (p) {
          var n = current;
          var pn = panel(n);
          var t = splitTitle(p.title);
          var img = p.featured_image ? (p.featured_image.indexOf("//") === 0 ? "https:" + p.featured_image : p.featured_image) : "";
          var existing = pn.querySelector('input[value="' + p.id + '"]');
          var input;
          if (existing) input = existing;
          else {
            var card = pickCard("gf-box-" + boxId + "-" + n, {
              id: p.id, title: t.name, colour: t.colour, price: p.price,
              img: img ? img + (img.indexOf("?") > -1 ? "&" : "?") + "width=400" : "",
              option: (p.options && p.options[0] && (p.options[0].name || p.options[0])) || "",
              variants: (p.variants || []).map(function (v) { return { id: v.id, title: v.title, available: v.available, price: v.price }; })
            });
            card.setAttribute("data-from-search", "true");
            pn.insertBefore(card, pn.firstChild);
            input = card.querySelector("input");
          }
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
          track("gifting_box_search_pick", { step: n, handle: handle });
        })
        .catch(function () { b.disabled = false; if (searchStatus) searchStatus.textContent = "Couldn't load that piece. Try another."; });
    });

    if (note && count) {
      var max = Number(note.getAttribute("maxlength")) || 200;
      var upd = function () { count.textContent = note.value.length + " / " + max; };
      note.addEventListener("input", upd); upd();
    }

    if (btn) btn.addEventListener("click", function () {
      var picks = slots.map(function (s) { return chosen(Number(s.getAttribute("data-gf-slot"))); });
      if (picks.some(function (p) { return !p; })) return;
      var missing = -1;
      picks.forEach(function (p, i) { if (missing < 0 && !p.id) missing = i; });
      if (missing > -1) {
        var ms = slots[missing];
        var msel = ms.querySelector("[data-gf-variant-select]");
        ms.setAttribute("data-error", "true");
        if (status) { status.textContent = "Choose a size for the " + picks[missing].title + "."; status.setAttribute("data-tone", "error"); }
        if (msel) { msel.focus(); if (msel.getBoundingClientRect().top < 120) msel.scrollIntoView({ behavior: "smooth", block: "center" }); }
        return;
      }
      if (status) status.removeAttribute("data-tone");
      var gid = "GB-" + Date.now().toString(36).toUpperCase();
      var msg = note ? note.value.trim() : "";
      var items = picks.map(function (p, i) {
        var props = { _gift_box: gid };
        if (i === 0 && msg) props["Gift note"] = msg;
        return { id: p.id, quantity: 1, properties: props };
      });
      btn.disabled = true;
      if (status) status.textContent = "Adding your gift set…";
      addToCart(items).then(function () {
        if (status) status.textContent = "Added to your bag.";
        track("gifting_box_add", { box: gid, items: picks.map(function (p) { return p.title; }) });
        refreshAndOpenCart();
      }).catch(function (err) {
        if (status) status.textContent = err.message || "Could not add to bag.";
      }).then(function () { update(); });
    });

    showStep(1);
    update();
  }

  /* ----------------------------------------------------------------- SETS */
  function bindSet(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-gf-set-item]"));
    var sizes = root.querySelector("[data-gf-set-sizes]");
    var btn = root.querySelector("[data-gf-add]");
    var status = root.querySelector("[data-gf-status]");
    var rows = items.map(function (el) {
      var vars = parse(el.getAttribute("data-variants"), []);
      var row = { el: el, vars: vars, title: el.getAttribute("data-title"), select: null };
      if (vars.length > 1 && sizes) {
        var w = variantSelect(vars, el.getAttribute("data-option"), el.getAttribute("data-title") + ": " + (el.getAttribute("data-option") || "Size").toLowerCase(), "gf-set");
        if (w) { sizes.appendChild(w); row.select = w.querySelector("select"); }
      }
      return row;
    });
    var soldOut = rows.some(function (r) { return !r.vars.some(function (v) { return v.available; }); });

    function ids() {
      return rows.map(function (r) {
        if (r.vars.length === 1) return r.vars[0].available ? r.vars[0].id : null;
        return r.select && r.select.value ? Number(r.select.value) : null;
      });
    }
    function update() {
      if (!btn) return;
      if (soldOut) { btn.disabled = true; if (status) status.textContent = "One piece in this set is sold out."; return; }
      btn.disabled = ids().some(function (id) { return !id; });
    }
    root.addEventListener("change", update);
    if (btn) btn.addEventListener("click", function () {
      var list = ids();
      if (list.some(function (id) { return !id; })) return;
      btn.disabled = true;
      if (status) status.textContent = "Adding the set…";
      addToCart(list.map(function (id) { return { id: id, quantity: 1, properties: { _gift_set: root.getAttribute("data-set-name") || "" } }; }))
        .then(function () {
          if (status) status.textContent = "Added to your bag.";
          track("gifting_set_add", { set: root.getAttribute("data-set-name") });
          refreshAndOpenCart();
        })
        .catch(function (err) { if (status) status.textContent = err.message || "Could not add to bag."; })
        .then(update);
    });
    update();
  }

  /* --------------------------------------------------------------- E-GIFT */
  function bindEgift(root) {
    var btn = root.querySelector("[data-gf-egift-go]");
    var amt = root.querySelector("[data-gf-card-amount]");
    root.addEventListener("change", function (e) {
      var input = e.target.closest('input[type="radio"]');
      if (!input) return;
      if (btn) btn.setAttribute("href", input.getAttribute("data-url"));
      if (amt) amt.textContent = input.getAttribute("data-label") || "";
    });
  }

  function init(scope) {
    var s = scope || document;
    var bind = function (sel, fn) {
      Array.prototype.forEach.call(s.querySelectorAll(sel), function (el) {
        if (el.__gfBound) return;
        el.__gfBound = true;
        fn(el);
      });
    };
    bind("[data-gf-finder]", bindFinder);
    bind("[data-gf-box]", bindBox);
    bind("[data-gf-set]", bindSet);
    bind("[data-gf-egift]", bindEgift);
  }

  window.MMGifting = { init: init };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { init(); });
  else init();
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
})();
